import {
  PaginatedSpeakerResponseList,
  UserSpeakerListAPIQueryParams,
  assetsChaptersList,
  userSpeakerList,
  ChapterListResponse
} from '@goldcast/api/content';
import { isString } from 'radash';
import { ChapterGenerationStatus, RawWord, SpeakerWithDetails, TranscriptContextData } from './TranscriptContextTypes';
import { speakerLightBgColors, speakerBgColors, speakerTextColors, speakerHoverBgColors } from './SpeakerColors';
import { FormattedDefaultTranscript, Word } from '@/domains/transcript';
import { currentClip } from '@/stores/clip';
import { getRecordingSubtitleUrl, getSpeakerImage } from '@/libs/utils';
import { core } from '@/stores/core';
import { showErrorToast } from '@/libs/toast/toast';
import { updatePlayerStoreWithId } from '@/stores/playerV2';
import { fullTranscriptStore } from '@/stores/fullTranscriptStore';
import { Pronouns } from '@/domains/speaker';
import { getAlignedRecordings } from '@/App/remotion/MainPlayer/stores/alignedRecordings';
import { isEntitySharedVideo } from '@/libs/activityUtils';
import { mergeWindows, subtractRanges } from '@/components/molecules/Transcript/EditTranscriptMenu/utils';

export function loadSpeakers(queryParams: UserSpeakerListAPIQueryParams) {
  return userSpeakerList({ queryParams })
    .then(speakers => {
      const processedSpeakers = ((speakers as unknown as PaginatedSpeakerResponseList['results']) || []).reduce(
        (acc, speaker) => {
          return { ...acc, [speaker.label as unknown as string]: speaker };
        },
        {}
      );
      return processedSpeakers;
    })
    .catch(error => {
      showErrorToast(
        'Uh-oh! We hit a snag loading the speakers. Quick check: is your internet feeling okay? If it looks good, give it another shot.'
      );
      console.error('Error loading speakers', error);
      return {};
    });
}

export const defaultState: TranscriptContextData = {
  transcript: [],
  end: 0,
  speakersWithDetails: {},
  usedSpeakers: [],
  timeSrtIndexArray: [],
  chapterData: undefined,
  chapterGenerationStatus: ChapterGenerationStatus.NOT_STARTED,
  chaptersLastUpdatedAt: null,
  isTranscriptInitialized: true
};

function getFilteredWords(words: RawWord[], clip_start_time?: number, clip_end_time?: number) {
  return clip_start_time === undefined || clip_end_time === undefined
    ? words
    : words.filter(({ start_time, end_time }, index) => {
        const actual_start_time = start_time ? start_time : words[index - 1]?.start_time;
        const actual_end_time = end_time ? end_time : words[index + 1]?.end_time;

        return (
          clip_start_time === undefined ||
          clip_end_time === undefined ||
          (+actual_start_time >= clip_start_time && +actual_end_time <= clip_end_time)
        );
      });
}

interface TranscriptReturn {
  transcript: Word[];
  /**
   * This is required for showing speaker identification at clip level
   */
  fullTranscript: Word[];
}

export function getTranscript(
  cacheSuffix: string,
  event: string,
  broadcast: string,
  clip_start_time?: number,
  clip_end_time?: number
): Promise<TranscriptReturn> {
  return fetch(getRecordingSubtitleUrl(event, broadcast)(`transcript.json?${cacheSuffix}`), { cache: 'force-cache' })
    .then(res => {
      if (res.status >= 400) {
        showErrorToast(
          "Oops! We're having trouble loading the transcript. Please check your connection and try again."
        );
      }
      return res.json();
    })
    .then(words => {
      if (!Array.isArray(words)) {
        return {
          transcript: [],
          fullTranscript: []
        };
      }

      const fullTranscript = getTranscriptDataInRequiredFormat(words);
      const filteredWords = getFilteredWords(words, clip_start_time, clip_end_time);

      fullTranscriptStore.update(() => fullTranscript);

      return {
        transcript: getTranscriptDataInRequiredFormat(filteredWords),
        fullTranscript
      };
    });
}

function getTranscriptDataInRequiredFormat(words: RawWord[]): FormattedDefaultTranscript {
  return words.map(({ alternatives, start_time, end_time, ...word }: RawWord, index: number) => {
    const startTime = words[index + 1]?.start_time;
    const endTime = words[index - 1]?.end_time;

    return {
      ...word,
      start_time: start_time ? +start_time : endTime ? +endTime : endTime,
      end_time: end_time ? +end_time : startTime ? +startTime : startTime,
      content: alternatives[0].content ?? '',
      index
    };
  });
}

export const getSpeakersPayloadParams = (contentId: string, isNotUpload: boolean): UserSpeakerListAPIQueryParams => {
  if (isNotUpload) {
    return { broadcast: contentId };
  }
  return { upload: contentId };
};

export function getSpeakersParams(contentId: string): UserSpeakerListAPIQueryParams {
  const { content } = core.getSnapshot();
  if (content && content?.media_source_type !== 'UPLOAD') {
    return { broadcast: contentId };
  }
  return { upload: contentId };
}

export function loadChapters(broadcastId: string) {
  return assetsChaptersList({
    queryParams: {
      content_id: broadcastId
    }
  })
    .then(chapters => {
      return chapters;
    })
    .catch(err => {
      console.error('Error loading chapters', err);
      return {
        chapter_generation_status: ChapterGenerationStatus.NOT_STARTED,
        chapters: [],
        latest_update_at: null
      };
    }) as Promise<ChapterListResponse | any>;
}

export function initTranscript(
  cacheSuffix: string,
  eventId: string,
  broadcastId: string
): Promise<[TranscriptReturn, Record<string, SpeakerWithDetails>, ChapterListResponse]> {
  return Promise.allSettled([
    getTranscript(cacheSuffix, eventId!, broadcastId!),
    loadSpeakers(getSpeakersParams(broadcastId!)),
    loadChapters(broadcastId!)
  ]).then(([transcriptResult, speakersResult, chaptersResult]) => {
    if (transcriptResult.status === 'rejected') {
      throw transcriptResult.reason;
    }

    return [
      transcriptResult.value,
      speakersResult.status === 'rejected' ? {} : speakersResult.value,
      chaptersResult.status === 'rejected' ? [] : chaptersResult.value
    ];
  });
}

export const getSpeakerColorAttributes = (index: number) => {
  return {
    color: speakerTextColors[index % speakerTextColors.length],
    bgColor: speakerBgColors[index % speakerBgColors.length],
    lightBgColor: speakerLightBgColors[index % speakerLightBgColors.length],
    hoverBgColor: speakerHoverBgColors[index % speakerHoverBgColors.length]
  };
};

export const formSpeakerDetails = (
  sessionSpeakers: Record<string, SpeakerWithDetails>
): Record<string, SpeakerWithDetails> => {
  const speakerIds = Object.keys(sessionSpeakers);
  const speakers: Record<string, SpeakerWithDetails> = {};

  speakerIds.forEach((id, index) => {
    const speaker = sessionSpeakers[id];
    speakers[id] = {
      ...speaker,
      key: id,
      id: speaker?.id ?? index,
      profile_picture_url: getSpeakerImage(speaker?.profile_picture_url),
      speakingSlots: {},
      ...getSpeakerColorAttributes(index)
    };
  });

  return speakers;
};

const updateSpeakingSlotsWithSharedVideo = ({
  speakers,
  broadcastId
}: {
  speakers: Record<string, SpeakerWithDetails>;
  broadcastId: string;
}) => {
  const alignedRecordings = getAlignedRecordings(broadcastId);
  const sharedVideos = alignedRecordings.filter(recording => isEntitySharedVideo(recording.entity));
  if (sharedVideos.length === 0) {
    return speakers;
  }
  const sharedVideoSpeakers = new Set(
    sharedVideos
      // TODO: @@HiteshChoudhary0098 this needs to be revisited. What backend stopped sending types for transcript mapping.
      // @ts-ignore
      .map(recording => recording.transcript_mapping?.speaker_labels?.[0])
      .filter(speakerKey => isString(speakerKey))
  );

  const stageTimeSpansForSharedVideo = sharedVideos
    .map(
      recording => recording.stage_time_spans?.map(span => [span.start_time, span.end_time] as [number, number]) ?? []
    )
    .flat();

  const mergedSortedTimeSpansForSharedVideo = mergeWindows(stageTimeSpansForSharedVideo);

  Object.keys(speakers).forEach(speakerKey => {
    if (sharedVideoSpeakers.has(speakerKey)) {
      return;
    }
    const { speakingSlots } = speakers[speakerKey];
    const mergedAndSortedSpeakingSlotsArray = mergeWindows(
      Object.entries(speakingSlots).map(([start, end]) => [+start, end])
    );

    const updatedSpeakingSlots = subtractRanges(mergedAndSortedSpeakingSlotsArray, mergedSortedTimeSpansForSharedVideo);
    speakers[speakerKey].speakingSlots = Object.fromEntries(updatedSpeakingSlots);
  });

  return speakers;
};

export const populateSpeakersWithSpeakingSlots = ({
  speakers,
  transcriptResult,
  broadcastId
}: {
  speakers: Record<string, SpeakerWithDetails>;
  transcriptResult: TranscriptReturn;
  broadcastId: string;
}) => {
  const fullTranscript = transcriptResult.fullTranscript;
  let start: number | null = null;
  let end = 0;
  fullTranscript.forEach((word, index) => {
    start = start ?? word.start_time;
    end = word.end_time ?? end;
    if (word.speaker_label !== fullTranscript[index + 1]?.speaker_label) {
      // Create speaker if it doesn't exist
      if (!speakers[word.speaker_label]) {
        const speakerIndex = Object.keys(speakers).length;
        speakers[word.speaker_label] = {
          key: word.speaker_label,
          id: speakerIndex.toString(),
          company: '',
          first_name: 'Speaker ' + speakerIndex,
          last_name: '',
          title: '',
          pronouns: Pronouns.THEY,
          profile_picture_url: '',
          speakingSlots: {},
          parent_id: null,
          ...getSpeakerColorAttributes(speakerIndex)
        };
      }
      speakers[word.speaker_label].speakingSlots[start ?? '0'] = end;
      start = null;
    }
  });
  updateSpeakingSlotsWithSharedVideo({ speakers, broadcastId });
  return { speakers, end };
};

export const formTranscriptContextData = ({
  transcriptResult,
  sessionSpeakers,
  chapterData,
  clipId,
  broadcastId
}: {
  transcriptResult: TranscriptReturn;
  sessionSpeakers: Record<string, SpeakerWithDetails>;
  chapterData: ChapterListResponse;
  clipId: string;
  broadcastId: string;
}): TranscriptContextData => {
  const transcript = transcriptResult.transcript;

  const speakersWithEmptySpeakingSlots = formSpeakerDetails(sessionSpeakers);
  const { speakers, end } = populateSpeakersWithSpeakingSlots({
    speakers: speakersWithEmptySpeakingSlots,
    transcriptResult,
    broadcastId
  });

  const srtIndexes: number[] = [];
  const timeSrtIndexArray: TranscriptContextData['timeSrtIndexArray'] = [];

  transcript.forEach(word => {
    if (srtIndexes.includes(word.srt_index)) {
      return;
    }
    srtIndexes.push(word.srt_index);
    timeSrtIndexArray.push({ startTime: word.start_time, srtIndex: word.srt_index });
  });

  if (clipId) {
    updatePlayerStoreWithId(clipId, { currentSrtIndex: srtIndexes[0] });
  }

  return {
    end,
    transcript,
    speakersWithDetails: speakers,
    chapterData: chapterData?.chapters,
    chaptersLastUpdatedAt: chapterData.latest_update_at,
    chapterGenerationStatus: chapterData?.chapter_generation_status,
    usedSpeakers: getUsedSpeakers(speakers),
    timeSrtIndexArray,
    isTranscriptInitialized: false
  };
};

export function isDeleted(start_time: number, clipId) {
  const clip = currentClip.getSnapshot()[clipId];
  if (!clip?.asset_metadata?.excludes) {
    return false;
  }

  return clip.asset_metadata.excludes.reduce((time, [start, end]) => {
    const t = time || start_time;
    if (start <= t && end >= t) {
      return end;
    }
    return time;
  }, 0);
}

export function getUsedSpeakers(allSpeakers: Record<string, SpeakerWithDetails>): SpeakerWithDetails[] {
  return Object.values(allSpeakers).filter(speaker => !speaker.parent_id);
}

export function getUnusedSpeakers(allSpeakers: Record<string, SpeakerWithDetails>): SpeakerWithDetails[] {
  return Object.values(allSpeakers).filter(speaker => !!speaker.parent_id);
}

function getAllCachedSuffixesAndCurrentCacheSuffixKey() {
  const allCacheSuffixes = localStorage.getItem('transcriptCacheSuffix');
  const content = core.getSnapshot().content;

  if (!content) {
    throw new Error('Content not available in core store');
  }

  const { project_id: projectId, id: contentId } = content;
  const cacheSuffixKey = `${projectId}:${contentId}`;

  return { allCacheSuffixes, cacheSuffixKey };
}

export function getNewCacheSuffix() {
  const { allCacheSuffixes, cacheSuffixKey } = getAllCachedSuffixesAndCurrentCacheSuffixKey();

  let cacheSuffix = Math.random().toString();

  if (!allCacheSuffixes) {
    setNewCacheSuffix(cacheSuffix);
    return cacheSuffix;
  }

  const parsedCacheSuffixes = JSON.parse(allCacheSuffixes);
  const currentCacheSuffix = parsedCacheSuffixes[cacheSuffixKey];

  if (currentCacheSuffix) {
    cacheSuffix = currentCacheSuffix;
  } else {
    setNewCacheSuffix(cacheSuffix);
  }

  return cacheSuffix;
}

/**
 * Use this function to set the cache suffix for the transcript
 * This is required to invalidate the cache and fetch the latest transcript in case of MAIN/FULL transcript editing actions
 */
export function setNewCacheSuffix(cacheSuffix: string) {
  const { allCacheSuffixes, cacheSuffixKey } = getAllCachedSuffixesAndCurrentCacheSuffixKey();

  const parsedCacheSuffixes = JSON.parse(allCacheSuffixes || '{}');

  localStorage.setItem(
    'transcriptCacheSuffix',
    JSON.stringify({
      ...parsedCacheSuffixes,
      [cacheSuffixKey]: cacheSuffix
    })
  );
}

export function clearCacheSuffixForCurrentContent() {
  const content = core.getSnapshot().content;
  if (!content) {
    return;
  }
  const { allCacheSuffixes, cacheSuffixKey } = getAllCachedSuffixesAndCurrentCacheSuffixKey();

  const parsedCacheSuffixes = JSON.parse(allCacheSuffixes || '{}');

  delete parsedCacheSuffixes?.[cacheSuffixKey];

  localStorage.setItem(
    'transcriptCacheSuffix',
    JSON.stringify({
      ...parsedCacheSuffixes
    })
  );
}
